import { inject, injectable } from 'inversify';
import { TYPES } from '../di/types';
import type { GrubTelemetryService } from './monitoring/GrubTelemetryService';

/*
    This service allows us to capture the total length of time it takes to complete an interaction,
    such as taping on a menu item to the time the item is displayed.  Once an interaction is
    complete, we sent a custom event to Datadog so that we can capture timings to be used for SLOs.

    In addition, it allows sampling for interactions upon which we do not want to record every
    event.

    To record an interaction:
    1) make sure the start and complete event is configured in actionCompleteEventConfigs
    2) record the start event:  this.pageActionTimingService.record(PageTimingEvent.ADD_TO_BAG_CLICK);
    3) record the completed event:  this.pageActionTimingService.record(PageTimingEvent.BAG_DISPLAYED);
 */

export interface PageActionTimingService {
  record<T extends ActionData>(
    pageEvent: PageTimingEvent,
    actionData?: T,
  ): void;
}

export enum PageTimingEvent {
  SUBSCRIPTION_INFO_LOADED_START = 'SUBSCRIPTION_INFO_LOADED_START',
  SUBSCRIPTION_INFO_LOADED_END = 'SUBSCRIPTION_INFO_LOADED_END',
}

type ActionData = Record<string, any> | undefined;

interface PageActionTimingStartEvent {
  startTime: number;
  actionData: ActionData;
}

interface PageActionTimingStartEvents {
  [eventName: string]: PageActionTimingStartEvent;
}

type IActionEventConfig = {
  type: string;
  sampleRate?: number;
};

type IActionCompleteEventConfig = {
  [actionStartEvent in PageTimingEvent]?: IActionEventConfig;
};

type IActionCompleteEventConfigs = {
  [actionCompleteEvent in PageTimingEvent]?: IActionCompleteEventConfig;
};

@injectable()
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export class PageActionTimingServiceImpl implements PageActionTimingService {
  public static instance: PageActionTimingServiceImpl = null;

  /*
    This configuration is key'd by "complete events" and contains configuration for the
    cooresponding "start events".  This allows us to decouple the start/end events in the
    application.  When the complete event fires, we check to see if a cooresponding start
    event has occurred.  If it has, we read the config and process accordingly.
  */
  private static actionCompleteEventConfigs: IActionCompleteEventConfigs = {
    [PageTimingEvent.SUBSCRIPTION_INFO_LOADED_END]: {
      [PageTimingEvent.SUBSCRIPTION_INFO_LOADED_START]: {
        type: 'subscription_info_loaded',
      },
    },
  };

  private activeStartEvents: PageActionTimingStartEvents = {};

  constructor(
    @inject(TYPES.GrubTelemetryService)
    private readonly grubTelemetryService: GrubTelemetryService,
  ) {}

  public record<T extends ActionData>(
    pageEvent: PageTimingEvent,
    actionData?: T,
  ) {
    const actionCompleteEventConfig =
      PageActionTimingServiceImpl.actionCompleteEventConfigs[pageEvent];
    if (actionCompleteEventConfig) {
      this.recordActionCompleted<T>(actionCompleteEventConfig, actionData);
    } else {
      this.recordActionStarted<T>(pageEvent, actionData);
    }
  }

  private recordActionStarted<T extends ActionData>(
    pageEvent: PageTimingEvent,
    actionData?: T,
  ) {
    this.activeStartEvents[pageEvent] = {
      startTime: Date.now(),
      actionData,
    };
  }

  private recordActionCompleted<T extends ActionData>(
    actionCompleteEventConfig: IActionCompleteEventConfig,
    actionCompletedData?: T,
  ) {
    const matchingStartEventKeys = Object.keys(actionCompleteEventConfig);
    const activeStartEventKeys = Object.keys(this.activeStartEvents);
    const matchingActiveStartEventsKeys = activeStartEventKeys.filter(
      (k) => matchingStartEventKeys.indexOf(k) > -1,
    );
    matchingActiveStartEventsKeys.forEach((activeStartEventKey) => {
      const activeStartEvent = this.activeStartEvents[activeStartEventKey];
      const { startTime, actionData = {} } = activeStartEvent;
      const totalTime = Date.now() - startTime;
      let payload = {
        ...actionData,
        totalTime,
      };

      const { type, sampleRate } =
        actionCompleteEventConfig[activeStartEventKey];
      if (!sampleRate || Math.random() < sampleRate) {
        if (actionCompletedData) {
          payload = { ...payload, ...actionCompletedData };
        }
        this.grubTelemetryService.sendPageActionEvent({
          type,
          payload,
        });
      }
      delete this.activeStartEvents[activeStartEventKey];
    });
  }
}
